import { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react';
import { notifyOrderCart } from '../utils/orderFunnel';

export interface CartItem {
	productId: number;
	name: string;
	price: number;
	image: string;
	quantity: number;
}

export interface CartState {
	items: CartItem[];
	deliveryFee: number;
	total: number;
}

type CartAction =
	| { type: 'ADD_TO_CART'; payload: CartItem }
	| { type: 'REMOVE_FROM_CART'; payload: number }
	| { type: 'UPDATE_QUANTITY'; payload: { productId: number; quantity: number } }
	| { type: 'CLEAR_CART' }
	| { type: 'LOAD_CART'; payload: CartItem[] };

interface CartContextValue extends CartState {
	addToCart: (item: CartItem) => void;
	removeFromCart: (productId: number) => void;
	updateQuantity: (productId: number, quantity: number) => void;
	clearCart: () => void;
	getCartCount: () => number;
}

const DELIVERY_FEE = 14500;
const STORAGE_KEY = 'akku24_cart';

function calculateTotal(items: CartItem[]): number {
	const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
	return items.length > 0 ? subtotal + DELIVERY_FEE : 0;
}

function cartReducer(state: CartState, action: CartAction): CartState {
	let newItems: CartItem[];

	switch (action.type) {
		case 'ADD_TO_CART': {
			newItems = [{ ...action.payload, quantity: 1 }];
			return { ...state, items: newItems, total: calculateTotal(newItems) };
		}
		case 'REMOVE_FROM_CART':
			newItems = state.items.filter(i => i.productId !== action.payload);
			return { ...state, items: newItems, total: calculateTotal(newItems) };
		case 'UPDATE_QUANTITY':
			newItems = state.items.map(i =>
				i.productId === action.payload.productId
					? { ...i, quantity: action.payload.quantity }
					: i
			).filter(i => i.quantity > 0);
			return { ...state, items: newItems, total: calculateTotal(newItems) };
		case 'CLEAR_CART':
			return { items: [], deliveryFee: DELIVERY_FEE, total: 0 };
		case 'LOAD_CART':
			return { ...state, items: action.payload, total: calculateTotal(action.payload) };
		default:
			return state;
	}
}

function loadCartFromStorage(): CartItem[] {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			return JSON.parse(stored);
		}
	} catch {
		// ignore parse errors
	}
	return [];
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
	const initialItems = loadCartFromStorage();
	const [state, dispatch] = useReducer(cartReducer, {
		items: initialItems,
		deliveryFee: DELIVERY_FEE,
		total: calculateTotal(initialItems),
	});

	useEffect(() => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
	}, [state.items]);

	// Single choke point for the Order_Cart funnel beacon: every add-to-cart in the
	// app dispatches through here, mirroring the classic sites' ValasztAkku().
	const addToCart = (item: CartItem) => {
		dispatch({ type: 'ADD_TO_CART', payload: item });
		notifyOrderCart(item.productId);
	};
	const removeFromCart = (productId: number) => dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
	const updateQuantity = (productId: number, quantity: number) =>
		dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
	const clearCart = () => dispatch({ type: 'CLEAR_CART' });
	const getCartCount = () => state.items.reduce((sum, item) => sum + item.quantity, 0);

	return (
		<CartContext.Provider value={{ ...state, addToCart, removeFromCart, updateQuantity, clearCart, getCartCount }}>
			{children}
		</CartContext.Provider>
	);
}

export function useCartContext(): CartContextValue {
	const context = useContext(CartContext);
	if (!context) {
		throw new Error('useCartContext must be used within a CartProvider');
	}
	return context;
}
